/**
 * Shared attachment logic for both AI surfaces (analysis chat + smart entry). A pending image/PDF
 * becomes an Anthropic content block; this module owns the file limits, base64 read, validation, and
 * block-building so the two screens stay identical and DRY.
 */

export interface Attachment {
  name: string;
  kind: 'image' | 'document';
  media: string;
  data: string;
}

export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

/** Read a File as bare base64 (strip the `data:...;base64,` prefix the API doesn't want). */
export function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const s = String(fr.result);
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    fr.onerror = () => reject(new Error('read failed'));
    fr.readAsDataURL(file);
  });
}

/**
 * Validate + read a freshly-picked batch against the current set. Returns the accepted attachments
 * and, if anything was rejected, a single Persian error to surface.
 */
export async function acceptFiles(
  current: Attachment[],
  picked: File[],
): Promise<{ accepted: Attachment[]; error: string }> {
  const room = MAX_FILES - current.length;
  if (room <= 0) return { accepted: [], error: `حداکثر ${MAX_FILES} فایل` };
  const accepted: Attachment[] = [];
  let error = '';
  for (const f of picked.slice(0, room)) {
    const isImg = f.type.startsWith('image/');
    const isPdf = f.type === 'application/pdf';
    if (!isImg && !isPdf) {
      error = 'فقط تصویر یا PDF پشتیبانی می‌شود';
      continue;
    }
    if (f.size > MAX_FILE_BYTES) {
      error = `«${f.name}» بزرگ‌تر از ۵ مگابایت است`;
      continue;
    }
    accepted.push({
      name: f.name,
      kind: isImg ? 'image' : 'document',
      media: isImg ? f.type : 'application/pdf',
      data: await readBase64(f),
    });
  }
  return { accepted, error };
}

/** Anthropic content blocks for the attachments (image / document), in order. */
export function attachmentBlocks(files: Attachment[]): unknown[] {
  return files.map((f) =>
    f.kind === 'image'
      ? { type: 'image', source: { type: 'base64', media_type: f.media, data: f.data } }
      : {
          type: 'document',
          source: { type: 'base64', media_type: 'application/pdf', data: f.data },
        },
  );
}

/** Short chip label listing the attached files (for the user's echoed turn). */
export function attachmentLabel(files: Attachment[]): string {
  return files.map((f) => (f.kind === 'image' ? '🖼 ' : '📎 ') + f.name).join('  ');
}
